import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewWorkspacePage() {
  const navigate = useNavigate();
  const { switchWorkspace, refreshUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', plan_code: 'free', company_phone: '' });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Workspace name is required'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/workspaces`,
        { name: form.name.trim(), plan_code: form.plan_code, company_phone: form.company_phone.trim() },
        { withCredentials: true }
      );
      toast.success(`Workspace "${data.name}" created`);
      await refreshUser();
      await switchWorkspace(data.id, { persist: true });
      navigate('/dashboard', { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create workspace');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Buildings size={20} weight="duotone" className="text-blue-600" />
          <h1 className="text-xl font-bold" style={{ color: '#1a2e4a' }}>Create a new workspace</h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">Spin up a separate workspace for a different company, brand, or business unit. Your data stays isolated per workspace.</p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="ws-new-name">Workspace name</Label>
            <Input id="ws-new-name" data-testid="new-workspace-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="My New Company" />
          </div>
          <div>
            <Label htmlFor="ws-new-phone">Phone (optional)</Label>
            <Input id="ws-new-phone" value={form.company_phone} onChange={(e) => set('company_phone', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ws-new-plan">Plan</Label>
            <select id="ws-new-plan" className="w-full border rounded h-9 px-2 text-sm" value={form.plan_code} onChange={(e) => set('plan_code', e.target.value)}>
              <option value="free">Free (₹0/year · 5 employees)</option>
              <option value="starter">Starter (₹11,988/year · 25 employees)</option>
              <option value="pro">Pro (₹29,988/year · 100 employees)</option>
              <option value="business">Business (₹59,988/year · 500 employees)</option>
            </select>
            <p className="text-[10px] text-gray-500 mt-1">Paid plans require Razorpay payment (coming in Sprint B). Free plan activates immediately.</p>
          </div>
          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="new-workspace-submit">
              {submitting ? 'Creating…' : 'Create Workspace'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
